import PrivateRoute from "@components/routes/PrivateRoute";
import PublicRoute from "@components/routes/PublicRoute";
import BlankLayout from "@layouts/BlankLayout";
import LayoutWrapper from "@src/@core/layouts/components/layout-wrapper";
import HorizontalLayout from "@src/layouts/HorizontalLayout";
import VerticalLayout from "@src/layouts/VerticalLayout";
import { isObjEmpty } from "@utils";
import { Fragment, lazy } from "react";
import { Navigate } from "react-router-dom";
import RfidTool from "../../views/correctionTool/RfidTool";
import RegularOrderExport from "../../views/order/RegularOrderExport";
import RfidOrderExport from "../../views/rfidorder/ExportRfidOrder";
import HourlyProductSales from "../../views/salesstats/HourlyProductSales";
import CashierShift from "../../views/shift";

const Dashboard = lazy(() => import("../../views/Dashboard"));
const Error = lazy(() => import("../../views/Error"));
const OrderList = lazy(() => import("../../views/order"));
const EventList = lazy(() => import("../../views/event"));
const SmartGate = lazy(() => import("../../views/smartgate"));
const TablesList = lazy(() => import("../../views/table"));
const AccessoriesList = lazy(() => import("../../views/accessories"));
const GroupList = lazy(() => import("../../views/group"));
const AdminList = lazy(() => import("../../views/admin"));
const PartnersList = lazy(() => import("../../views/admin/partners.js"));
const RfCards = lazy(() => import("../../views/rfidcard"));
const TicketList = lazy(() => import("../../views/deals"));
const Login = lazy(() => import("../../views/auth/Login"));
const Register = lazy(() => import("../../views/Register"));
const ProductList = lazy(() => import("../../views/product"));
const CashierList = lazy(() => import("../../views/cashier"));
const PageNotFound = lazy(() => import("../../views/404.js"));
const KitchenList = lazy(() => import("../../views/kitchen"));
const RfIdOrders = lazy(() => import("../../views/rfidorder"));
const CategoryList = lazy(() => import("../../views/category"));
const LocationList = lazy(() => import("../../views/location"));
const LocationSales = lazy(() => import("../../views/salesstats"));
const RefundList = lazy(() => import("../../views/rfidcard/Refund"));
const RefundHistoryList = lazy(() => import("../../views/rfidcard/RefundHistory"));
const CreateRfidCards = lazy(() => import("../../views/rfidcard/CreateRfidCards"));
const FreezedOrderList = lazy(() => import("../../views/freezeOrder"));
const CorrectionTool = lazy(() => import("../../views/correctionTool"));
const TicketDetail = lazy(() => import("../../views/deals/TicketDetail"));
const OrderDetails = lazy(() => import("../../views/order/OrderDetails"));
const UpdateProfile = lazy(() => import("../../views/admin/UpdateProfile"));
const UpdatePassword = lazy(() => import("../../views/admin/UpdatePassword"));
const CashierSales = lazy(() => import("../../views/salesstats/CashierSales"));
const ProductSales = lazy(() => import("../../views/salesstats/ProductSales"));
const FreezedOrderDetail = lazy(() => import("../../views/freezeOrder/FreezedOrderDetails"));
const Settings = lazy(() => import("../../views/settings"));
const UpdateCompany = lazy(() => import("../../views/admin/UpdateCompany"));
const ManagementSales = lazy(() => import("../../views/salesstats/ManagementSales.js"));

const getLayout = {
  blank: <BlankLayout />,
  vertical: <VerticalLayout />,
  horizontal: <HorizontalLayout />,
};

const TemplateTitle = "%s - POS Admin Panel";
const DefaultRoute = "/dashboard";

const Routes = [
  {
    path: "/",
    index: true,
    element: <Navigate replace to={DefaultRoute} />,
  },
  {
    path: "/dashboard",
    element: <Dashboard />,
  },
  {
    path: "/login",
    element: <Login />,
    meta: {
      layout: "blank",
      restricted: true,
      publicRoute: true,
    },
  },
  {
    path: "/register",
    element: <Register />,
    meta: {
      layout: "blank",
    },
  },
  {
    path: "/password/update",
    element: <UpdatePassword />,
  },
  {
    path: "/company",
    element: <UpdateCompany />,
  },
  {
    path: "/admin",
    element: <UpdateProfile />,
  },
  {
    path: "/adminList",
    element: <AdminList />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/partnersList",
    element: <PartnersList />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/cashiers",
    element: <CashierList />,
  },
  {
    path: "/shifts",
    element: <CashierShift />,
  },
  {
    path: "/locations",
    element: <LocationList />,
  },
  {
    path: "/categories",
    element: <CategoryList />,
  },
  {
    path: "/groups",
    element: <GroupList />,
  },
  {
    path: "/kitchens",
    element: <KitchenList />,
  },
  {
    path: "/products",
    element: <ProductList />,
  },
  {
    path: "/events",
    element: <EventList />,
  },
  {
    path: "/smartgate",
    element: <SmartGate />,
  },
  {
    path: "/tables",
    element: <TablesList />,
  },
  {
    path: "/accessories",
    element: <AccessoriesList />,
  },
  {
    path: "/cards",
    element: <RfCards />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/refund",
    element: <RefundList />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/refund-history",
    element: <RefundHistoryList />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/createBulkCards",
    element: <CreateRfidCards />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/regularOrders",
    element: <OrderList />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/partner/regularOrders",
    element: <OrderList />,
    meta: {
      action: "read",
      resource: "partner",
    },
  },
  {
    path: "/regularOrderExport",
    element: <RegularOrderExport />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/partner/regularOrderExport",
    element: <RegularOrderExport />,
    meta: {
      action: "read",
      resource: "partner",
    },
  },
  {
    path: "/rfidOrdersexport",
    element: <RfidOrderExport />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/partner/rfidOrdersexport",
    element: <RfidOrderExport />,
    meta: {
      action: "read",
      resource: "partner",
    },
  },
  {
    path: "/rfidOrders",
    element: <RfIdOrders />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/partner/rfidOrders",
    element: <RfIdOrders />,
    meta: {
      action: "read",
      resource: "partner",
    },
  },
  {
    path: "/freezedOrders",
    element: <FreezedOrderList />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/partner/freezedOrders",
    element: <FreezedOrderList />,
    meta: {
      action: "read",
      resource: "partner",
    },
  },
  {
    path: "/orderDetails",
    element: <OrderDetails />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/freezeOrderDetails",
    element: <FreezedOrderDetail />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/salesReport",
    element: <LocationSales />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/partner/salesReport",
    element: <LocationSales />,
    meta: {
      action: "read",
      resource: "partner",
    },
  },
  {
    path: "/cashierSales",
    element: <CashierSales />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/partner/cashierSales",
    element: <CashierSales />,
    meta: {
      action: "read",
      resource: "partner",
    },
  },
  {
    path: "/productsales",
    element: <ProductSales />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/hourlproductsales",
    element: <HourlyProductSales />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/partner/productSales",
    element: <ProductSales />,
    meta: {
      action: "read",
      resource: "partner",
    },
  },
  {
    path: "/partner/hourlproductsales",
    element: <HourlyProductSales />,
    meta: {
      action: "read",
      resource: "partner",
    },
  },
  {
    path: "/managementSales",
    element: <ManagementSales />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/ticketList",
    element: <TicketList />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/ticketDetails",
    element: <TicketDetail />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/productTool",
    element: <CorrectionTool />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/RfidTool",
    element: <RfidTool />,
    meta: {
      action: "manage",
      resource: "superadmin",
    },
  },
  {
    path: "/error",
    element: <Error />,
    meta: {
      layout: "blank",
    },
  },
  {
    path: "/settings",
    element: <Settings />,
  },
  {
    path: "*",
    element: <PageNotFound />,
    meta: {
      layout: "blank",
      publicRoute: true,
    },
  },
];

const getRouteMeta = (route) => {
  if (isObjEmpty(route.element.props)) {
    if (route.meta) {
      return { routeMeta: route.meta };
    } else {
      return {};
    }
  }
};

const MergeLayoutRoutes = (layout, defaultLayout) => {
  const LayoutRoutes = [];

  if (Routes) {
    Routes.filter((route) => {
      let isBlank = false;
      if ((route.meta && route.meta.layout && route.meta.layout === layout) || ((route.meta === undefined || route.meta.layout === undefined) && defaultLayout === layout)) {
        const RouteTag = route.meta && route.meta.publicRoute ? PublicRoute : PrivateRoute;
        if (route.meta) {
          route.meta.layout === "blank" ? (isBlank = true) : (isBlank = false);
        }
        if (route.element) {
          const Wrapper = isObjEmpty(route.element.props) && isBlank === false ? LayoutWrapper : Fragment;
          route.element = (
            <Wrapper {...(isBlank === false ? getRouteMeta(route) : {})}>
              <RouteTag route={route}>{route.element}</RouteTag>
            </Wrapper>
          );
        }
        LayoutRoutes.push(route);
      }
      return LayoutRoutes;
    });
  }
  return LayoutRoutes;
};

const getRoutes = (layout) => {
  const defaultLayout = layout || "vertical";
  const layouts = ["vertical", "horizontal", "blank"];
  const AllRoutes = [];

  layouts.forEach((layoutItem) => {
    const LayoutRoutes = MergeLayoutRoutes(layoutItem, defaultLayout);
    AllRoutes.push({
      path: "/",
      element: getLayout[layoutItem] || getLayout[defaultLayout],
      children: LayoutRoutes,
    });
  });
  return AllRoutes;
};

export { DefaultRoute, getRoutes, Routes, TemplateTitle };

